/**
 * Object Field Component
 *
 * Renders a JSON editor for object properties with Monaco Editor
 */

import { Label } from '@/components/ui/label';
import { useState, useEffect, useRef } from 'react';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import { CodeMirrorEditor } from '../../node-editor/CodeMirrorEditor';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';

interface ObjectFieldProps {
  property: InferredPropertySchema;
  value: Record<string, any>;
  onChange: (value: Record<string, any>) => void;
  error?: string;
}

export function ObjectField({ property, value, onChange, error }: ObjectFieldProps) {
  const [jsonString, setJsonString] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const lastValueRef = useRef<any>(null);

  useEffect(() => {
    // Skip updating jsonString while the user is actively editing
    // This prevents the cursor jumping and text reformatting while typing
    if (isFocused) {
      return;
    }

    // Check if the value actually changed (deep comparison for objects)
    const valueJson = typeof value === 'string' ? value : JSON.stringify(value);
    const lastValueJson = typeof lastValueRef.current === 'string'
      ? lastValueRef.current
      : JSON.stringify(lastValueRef.current);

    if (valueJson === lastValueJson) {
      return;
    }

    lastValueRef.current = value;

    try {
      // If value is a string (e.g., contains template variables), display it as-is
      if (typeof value === 'string') {
        setJsonString(value);
        setParseError(null);
      } else if (!value || (typeof value === 'object' && Object.keys(value).length === 0 && !Array.isArray(value))) {
        // If value is null, undefined, or an empty object (but not an empty array), set empty string to show placeholder
        setJsonString('');
        setParseError(null);
      } else {
        // Otherwise, stringify the object
        setJsonString(JSON.stringify(value, null, 2));
        setParseError(null);
      }
    } catch (err) {
      setParseError('Invalid JSON');
    }
  }, [value, isFocused]);

  const handleChange = (newValue: string) => {
    setJsonString(newValue);

    // Clear any existing parse error while typing
    if (parseError) {
      setParseError(null);
    }

    // Check if the string contains template variables (e.g., {{ $node["..."].json.field }})
    const hasTemplateVariables = /\{\{.*?\}\}/.test(newValue);

    if (hasTemplateVariables) {
      // If it has template variables, don't validate JSON - just pass the string
      // The backend will interpolate variables before parsing
      onChange(newValue as any);
      return;
    }

    // Try to parse immediately for valid JSON (so the value is updated)
    // Validation errors will be shown on blur
    try {
      const parsed = JSON.parse(newValue);
      onChange(parsed);
    } catch {
      // Ignore while typing - validation happens on blur
    }
  };

  // Handle focus to prevent reformatting while editing
  const handleFocus = () => {
    setIsFocused(true);
  };

  // Validate on blur - better UX than validating while typing
  const handleBlur = (currentValue: string) => {
    setIsFocused(false);

    // Update lastValueRef with current jsonString so we can detect external changes
    try {
      const parsed = JSON.parse(currentValue);
      lastValueRef.current = parsed;
    } catch {
      lastValueRef.current = currentValue;
    }

    // Skip validation if empty or has template variables
    if (!currentValue || /\{\{.*?\}\}/.test(currentValue)) {
      setParseError(null);
      return;
    }

    try {
      const parsed = JSON.parse(currentValue);
      onChange(parsed);
      setParseError(null);
    } catch {
      setParseError('Invalid JSON format');
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label htmlFor={property.key}>
          {property.label}
          {property.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {property.helpText && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-sm">{property.helpText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {property.description && (
        <p className="text-sm text-muted-foreground">{property.description}</p>
      )}
      <CodeMirrorEditor
        value={jsonString}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        language="json"
        height="200px"
        placeholder={property.placeholder || '{\n  "key": "value"\n}'}
        showFormatButton={true}
        lineNumbers={true}
      />
      {(error || parseError) && (
        <p className="text-sm text-destructive">{error || parseError}</p>
      )}
    </div>
  );
}
